import type { RawComponent } from "./types.js";
import { collectFingerprintJsVisitorId } from "./fingerprintjsClient.js";
import { collectCanvas, collectWebgl, collectWebgpu } from "./collectors/canvasWebgl.js";
import { collectAudio } from "./collectors/audio.js";
import {
  collectCssFeatures,
  collectDeviceProfile,
  collectFonts,
  collectMediaCodecs,
  collectNavigatorComponents,
  type DeviceProfile,
} from "./collectors/navigatorInfo.js";
import { collectStorageComponents } from "./collectors/storage.js";
import { checkFingerprintProReuse } from "./fingerprintPrecheckClient.js";
import { identifyWithFingerprintPro } from "./fingerprintProClient.js";
import { getOrCreateIndexedDbId, getOrCreateLocalStorageId } from "./identifiers.js";

declare global {
  interface Window {
    turnstile?: {
      render(
        container: string | HTMLElement,
        options: { sitekey: string; action: string; callback: (token: string) => void },
      ): string;
    };
  }
}

const statusEl = document.getElementById("status") as HTMLElement;
const detailEl = document.getElementById("detail") as HTMLElement;

function setStatus(message: string, detail = ""): void {
  statusEl.textContent = message;
  detailEl.textContent = detail;
}

/**
 * Turns the machine-readable reason codes the API already returns into
 * something the person in front of the browser can act on. Deliberately
 * coarse: it never explains *which* signal tripped beyond the network
 * category, so it gives an evader nothing to tune against.
 *
 * Order matters. iCloud Private Relay is checked first because a relay user
 * is not on a VPN and must not be told to turn one off -- the relay path
 * suppresses the vpn/proxy/hosting rules, so those codes are never present
 * together, but the ordering makes that explicit rather than incidental.
 */
function explainOutcome(
  decision: "pass" | "review" | "reject" | "join_failure",
  reasons: string[],
): { title: string; detail: string } {
  const has = (code: string): boolean => reasons.includes(code);

  if (decision === "join_failure") {
    return {
      title: "Admission failed",
      detail:
        "Your checks passed, but Discord could not add you to the protected server. Please try verification again or contact a moderator.",
    };
  }

  if (has("NETWORK_APPLE_PRIVATE_RELAY")) {
    return decision === "pass"
      ? { title: "Verified", detail: "You now have access to the server. You can close this page." }
      : {
          title: "Awaiting review",
          detail:
            "Your connection uses iCloud Private Relay, so this verification needs a quick manual check. " +
            "A moderator will review it shortly — you do not need to change anything. " +
            "To be approved automatically instead, turn off Private Relay " +
            "(Settings → your name → iCloud → Private Relay, or Settings → Wi-Fi → ⓘ → Limit IP Address Tracking) and verify again.",
        };
  }

  const NETWORK_BLOCK_CODES = [
    "NETWORK_VPN",
    "NETWORK_PROXY",
    "NETWORK_TOR",
    "NETWORK_RELAY",
    "NETWORK_HOSTING",
    "NETWORK_RESIDENTIAL_PROXY",
  ];
  if (NETWORK_BLOCK_CODES.some(has)) {
    return {
      title: "Turn off your VPN or proxy",
      detail:
        "This server does not accept verification over a VPN, proxy, or similar service. " +
        "Disable it — including any always-on or per-app VPN, private-relay feature, or ad-blocking DNS app — " +
        "then reconnect to an ordinary network such as your home Wi-Fi or mobile data and click Verify again in Discord.",
    };
  }

  if (has("NETWORK_MOBILE_CARRIER")) {
    return {
      title: "Switch networks",
      detail:
        "This server does not accept verification over a mobile carrier connection. " +
        "Connect to Wi-Fi and click Verify again in Discord.",
    };
  }

  if (has("UNSUPPORTED_PRIVACY_OR_RESTRICTED_BROWSER") || has("INSUFFICIENT_FINGERPRINT_DATA")) {
    return {
      title: "Browser not supported",
      detail:
        "Your browser blocked the checks this server requires. Turn off strict anti-fingerprinting " +
        "or private-browsing mode, or try a standard browser window, then click Verify again in Discord.",
    };
  }

  if (decision === "pass") {
    return {
      title: "Verified",
      detail: "You now have access to the server. You can close this page.",
    };
  }
  if (decision === "review") {
    return {
      title: "Under review",
      detail: "A moderator will review your verification shortly. You can close this page.",
    };
  }
  return {
    title: "Verification failed",
    detail: "If you believe this is an error, contact a server moderator.",
  };
}

interface CollectedDeviceData {
  components: RawComponent[];
  deviceProfile: DeviceProfile;
}

async function collectAllComponents(): Promise<CollectedDeviceData> {
  const [fpjs, webgl, webgpu, audio, storage] = await Promise.all([
    collectFingerprintJsVisitorId(),
    Promise.resolve(collectWebgl()),
    collectWebgpu(),
    collectAudio(),
    collectStorageComponents(),
  ]);

  const components = [
    fpjs,
    collectCanvas(),
    ...webgl,
    webgpu,
    audio,
    ...collectNavigatorComponents(),
    collectMediaCodecs(),
    collectCssFeatures(),
    collectFonts(),
    ...storage,
  ];

  return {
    components,
    deviceProfile: await collectDeviceProfile(components),
  };
}

async function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("failed to load Turnstile"));
    document.head.appendChild(script);
  });
}

interface FrontendConfig {
  turnstileSiteKey: string;
  fingerprintProPublicKey: string | null;
  fingerprintProRegion: "us" | "eu" | "ap";
  fingerprintProReuseEnabled: boolean;
}

async function submit(
  turnstileToken: string,
  components: RawComponent[],
  deviceProfile: DeviceProfile,
  frontendConfig: FrontendConfig,
  identifiers: { indexeddb: string | null; localstorage: string | null },
): Promise<void> {
  setStatus("Verifying…", "Checking your device and network.");

  const { indexeddb, localstorage } = identifiers;

  // Asked at submission time, NOT during page load, because the answer has a
  // shelf life: the server only reuses an identification younger than
  // FINGERPRINT_PRO_REUSE_WINDOW_MS (120s by default), and a user can easily
  // spend longer than that on the Turnstile challenge. A skip decided at page
  // load and acted on after a slow solve is the worst of both -- the browser
  // withholds its requestId for an event /api/verify then rejects as stale,
  // and the attempt carries no Fingerprint Pro evidence at all, silently
  // dropping the 0.98-weight visitor ID from correlation. Asking here keeps
  // the question and the answer inside the same window.
  const skipFingerprintPro =
    frontendConfig.fingerprintProReuseEnabled && frontendConfig.fingerprintProPublicKey
      ? await checkFingerprintProReuse(indexeddb, localstorage)
      : false;

  // Deliberately here and not in collectAllComponents(): the Fingerprint Pro
  // free tier bills per identification, so only an actual submission spends
  // one, never a page view that is opened and abandoned. skipFingerprintPro
  // means the server already holds a fresh identification for this device, so
  // this submission does not need to buy another -- see
  // fingerprintPrecheckClient.ts.
  const proIdentification =
    frontendConfig.fingerprintProPublicKey && !skipFingerprintPro
      ? await identifyWithFingerprintPro(
          frontendConfig.fingerprintProPublicKey,
          frontendConfig.fingerprintProRegion,
        )
      : null;

  let response: Response;
  try {
    response = await fetch("/api/verify", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        turnstileToken,
        jsEnabled: true,
        identifiers: { indexeddb, localstorage },
        userAgent: navigator.userAgent,
        // Moderator-facing descriptive metadata only. The risk engine does not
        // consume these values; correlation continues to use `components`.
        deviceProfile,
        // Only the request id — the visitor id is resolved server-side.
        fingerprintProRequestId: proIdentification?.requestId ?? null,
        components,
      }),
    });
  } catch {
    setStatus("Network error", "Could not reach the verification server. Please try again.");
    return;
  }

  if (response.status === 401) {
    setStatus("Session expired", "Please go back to Discord and click Verify again.");
    return;
  }
  if (response.status === 409) {
    setStatus("Already used", "This verification attempt was already completed.");
    return;
  }
  if (!response.ok) {
    setStatus(
      "Verification failed",
      "Please try again, or contact a server moderator if this persists.",
    );
    return;
  }

  const body = (await response.json()) as {
    decision: "pass" | "review" | "reject" | "join_failure";
    reasons?: string[];
  };
  const outcome = explainOutcome(body.decision, body.reasons ?? []);
  setStatus(outcome.title, outcome.detail);
}

async function main(): Promise<void> {
  setStatus("Loading…", "Preparing verification.");

  const [configResponse, collected, indexeddb, localstorage] = await Promise.all([
    fetch("/api/frontend-config"),
    collectAllComponents(),
    getOrCreateIndexedDbId(),
    Promise.resolve(getOrCreateLocalStorageId()),
  ]);
  const identifiers = { indexeddb, localstorage };

  if (!configResponse.ok) {
    setStatus("Error", "Could not load verification configuration.");
    return;
  }
  const frontendConfig = (await configResponse.json()) as FrontendConfig;

  setStatus("Almost there…", "Complete the check below.");
  await loadTurnstileScript();

  const container = document.getElementById("turnstile-container");
  if (!container || !window.turnstile) {
    setStatus("Error", "Could not load the verification widget.");
    return;
  }

  window.turnstile.render(container, {
    sitekey: frontendConfig.turnstileSiteKey,
    action: "verify",
    callback: (token: string) => {
      void submit(
        token,
        collected.components,
        collected.deviceProfile,
        frontendConfig,
        identifiers,
      );
    },
  });
}

main().catch(() => {
  setStatus("Error", "Something went wrong. Please refresh and try again.");
});
